/**
 * KYCCompliancePage (v37 batch 1.5 — May 21, 2026)
 *
 * HR/Admin dashboard listing every active staff with their KYC completion %.
 * Sorted by % ascending so the most-at-risk staff show first. Supports bulk
 * select + "Send Reminder Email" action via Resend (configured in backend).
 */
import { useEffect, useState, useMemo } from 'react';
import { v37API } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
  ShieldCheck, ShieldAlert, Mail, Search, Loader2, CheckCircle2, AlertTriangle, Send, Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { cn } from '../lib/utils';

const KYCCompliancePage = () => {
  const { user } = useAuth();
  const isHR = user?.is_super_admin || ['hospital_admin', 'hr'].includes(user?.role_code) || ['Hospital Admin', 'HR'].includes(user?.role_name);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlyIncomplete, setOnlyIncomplete] = useState(true);
  const [query, setQuery] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await v37API.kycCompliance(onlyIncomplete);
      setData(res.data);
      setSelectedIds(new Set());
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      // v37 batch 1.5 fix: distinguish "no hospital selected" from "real failure" so we don't
      // mislead the user with the 'All staff are compliant 🎉' empty state.
      if (err.response?.status === 400 && /no hospital/i.test(detail)) {
        setData({ needsHospital: true });
      } else {
        toast.error(detail || 'Failed to load compliance');
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlyIncomplete]);

  const departments = useMemo(() => {
    if (!data?.staff) return ['all'];
    return ['all', ...Array.from(new Set(data.staff.map(s => s.department).filter(Boolean)))];
  }, [data]);

  const filtered = useMemo(() => {
    if (!data?.staff) return [];
    const q = query.trim().toLowerCase();
    return data.staff.filter(s => {
      if (deptFilter !== 'all' && s.department !== deptFilter) return false;
      if (!q) return true;
      return s.name.toLowerCase().includes(q) || s.employee_code.toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q);
    });
  }, [data, query, deptFilter]);

  const toggle = (id) => setSelectedIds(s => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAll = () => setSelectedIds(new Set(filtered.filter(r => !r.is_complete).map(r => r.staff_id)));
  const clearSel = () => setSelectedIds(new Set());

  const handleSend = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one staff to remind');
      return;
    }
    if (!window.confirm(`Send KYC reminder email to ${selectedIds.size} staff?`)) return;
    setSending(true);
    try {
      const res = await v37API.sendKycReminders(Array.from(selectedIds));
      const { sent, failed, skipped_complete, email_configured } = res.data;
      if (!email_configured) {
        toast.warning('Email service not configured (RESEND_API_KEY missing). No reminders sent.');
      } else {
        toast.success(`Sent ${sent} reminder${sent === 1 ? '' : 's'}${failed ? ` · ${failed} failed` : ''}${skipped_complete ? ` · ${skipped_complete} skipped (already complete)` : ''}`);
      }
      setSelectedIds(new Set());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  if (!isHR) {
    return (
      <div className="bg-white border border-amber-200 rounded-xl p-8 text-center" data-testid="kyc-compliance-forbidden">
        <ShieldAlert size={28} className="mx-auto text-amber-500 mb-2" />
        <p className="text-sm font-semibold text-slate-700">Access restricted</p>
        <p className="text-xs text-slate-500 mt-1">Only HR and Hospital Admins can view KYC compliance.</p>
      </div>
    );
  }

  // v37 batch 1.5 — Super Admin without a selected hospital
  if (data?.needsHospital) {
    return (
      <div className="bg-white border border-blue-200 rounded-xl p-10 text-center" data-testid="kyc-needs-hospital">
        <ShieldAlert size={32} className="mx-auto text-blue-500 mb-3" />
        <p className="text-base font-semibold text-slate-800">Pick a hospital to view KYC compliance</p>
        <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Use the <b>Manage Hospital</b> picker at the top-left of the sidebar to select a hospital — the dashboard will then populate with the staff list and completion percentages.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="kyc-compliance-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">KYC Compliance</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Track staff KYC profile completion and send bulk reminders. Sorted by completion % ascending.
          </p>
        </div>
      </div>

      {/* KPI strip */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide font-semibold text-slate-500"><Users size={11} /> Total Staff</div>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1" data-testid="kyc-kpi-total">{data.total_staff}</p>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide font-semibold text-slate-500"><CheckCircle2 size={11} /> Compliant</div>
            <p className="text-2xl font-bold text-emerald-600 mt-1" data-testid="kyc-kpi-complete">{data.complete_count}</p>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide font-semibold text-slate-500"><AlertTriangle size={11} /> Incomplete</div>
            <p className="text-2xl font-bold text-amber-600 mt-1" data-testid="kyc-kpi-incomplete">{data.incomplete_count}</p>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide font-semibold text-slate-500"><ShieldCheck size={11} /> Compliance</div>
            <p className="text-2xl font-bold text-blue-600 mt-1" data-testid="kyc-kpi-pct">{data.compliance_pct}%</p>
            <div className="mt-2 w-full h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all" style={{ width: `${data.compliance_pct}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name, employee code or email…" className="pl-9 h-9" data-testid="kyc-search" />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="h-9 w-44" data-testid="kyc-dept-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{departments.map(d => <SelectItem key={d} value={d}>{d === 'all' ? 'All Departments' : d}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={onlyIncomplete ? 'incomplete' : 'all'} onValueChange={v => setOnlyIncomplete(v === 'incomplete')}>
          <SelectTrigger className="h-9 w-52" data-testid="kyc-incomplete-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="incomplete">Incomplete only</SelectItem>
            <SelectItem value="all">All staff</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-9" onClick={selectAll} data-testid="kyc-select-all">Select All Incomplete</Button>
          {selectedIds.size > 0 && (
            <Button variant="outline" size="sm" className="h-9" onClick={clearSel} data-testid="kyc-clear-sel">Clear ({selectedIds.size})</Button>
          )}
          <Button
            size="sm"
            className="h-9 gap-1.5 bg-emerald-600 hover:bg-emerald-700"
            disabled={selectedIds.size === 0 || sending}
            onClick={handleSend}
            data-testid="kyc-send-reminders-btn">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? 'Sending…' : `Send Reminder${selectedIds.size > 1 ? 's' : ''} ${selectedIds.size > 0 ? `(${selectedIds.size})` : ''}`}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 gap-2"><Loader2 className="animate-spin" size={20} /> Loading compliance…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <CheckCircle2 size={32} className="mx-auto text-emerald-400 mb-2" />
            <p className="text-sm font-semibold text-slate-700">
              {onlyIncomplete ? 'All staff are KYC-compliant 🎉' : 'No matching staff'}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {onlyIncomplete ? 'Switch to "Show all staff" above to see the compliant staff.' : 'Try a different search or department.'}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th className="px-3 py-2.5 text-left w-8"><input type="checkbox" onChange={(e) => e.target.checked ? selectAll() : clearSel()} className="rounded" data-testid="kyc-table-select-all" /></th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Staff</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Department</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Email</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Missing</th>
                <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Completion</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const sel = selectedIds.has(r.staff_id);
                const status = r.is_complete ? 'complete' : r.completion_pct >= 70 ? 'partial' : 'low';
                return (
                  <tr key={r.staff_id} className={cn(
                    'border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30',
                    sel && 'bg-emerald-50/40 dark:bg-emerald-900/10'
                  )} data-testid={`kyc-row-${r.staff_id}`}>
                    <td className="px-3 py-2.5"><input type="checkbox" checked={sel} onChange={() => toggle(r.staff_id)} disabled={r.is_complete} className="rounded" data-testid={`kyc-checkbox-${r.staff_id}`} /></td>
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-slate-900 dark:text-slate-100">{r.name}</p>
                      <p className="text-[10px] font-mono text-slate-400">{r.employee_code}</p>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-300">{r.department}<br /><span className="text-[10px] text-slate-400">{r.designation}</span></td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-300 break-all">{r.email}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {r.missing_required.slice(0, 5).map(m => (
                          <span key={m.key} className="text-[9px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">{m.label}</span>
                        ))}
                        {r.missing_encouraged.slice(0, 3).map(m => (
                          <span key={m.key} className="text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">{m.label}</span>
                        ))}
                        {r.missing_required.length === 0 && r.missing_encouraged.length === 0 && (
                          <span className="text-[10px] text-emerald-600">All filled</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden min-w-[80px]">
                          <div className={cn(
                            'h-full transition-all',
                            status === 'complete' ? 'bg-emerald-500' :
                            status === 'partial' ? 'bg-amber-500' : 'bg-red-500'
                          )} style={{ width: `${r.completion_pct}%` }} />
                        </div>
                        <span className={cn(
                          'text-xs font-bold w-9 text-right',
                          status === 'complete' ? 'text-emerald-600' :
                          status === 'partial' ? 'text-amber-600' : 'text-red-600'
                        )} data-testid={`kyc-pct-${r.staff_id}`}>{r.completion_pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-[10px] text-slate-400 text-right flex items-center justify-end gap-1">
        <Mail size={10} /> Reminder emails are sent via Resend when configured. Bulk action is audit-logged.
      </p>
    </div>
  );
};

export default KYCCompliancePage;
